"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Check, X, Rocket } from "lucide-react"
import { isComingSoon } from "@/lib/coming-soon"

type BillingCycle = "monthly" | "annual"

interface Plan {
    name: string
    priceMonthly: string
    priceAnnual: string
    description: string
    cta: string
    href: string
    featured: boolean
}

const plans: Plan[] = [
    {
        name: "Free",
        priceMonthly: "0",
        priceAnnual: "0",
        description: "Kick the tires. No credit card required.",
        cta: "Start Free",
        href: "/register",
        featured: false,
    },
    {
        name: "Pro",
        priceMonthly: "49",
        priceAnnual: "490",
        description: "For engineers who take monitoring seriously.",
        cta: "Start Free, Upgrade Later",
        href: "/register",
        featured: true,
    },
    {
        name: "Business",
        priceMonthly: "99",
        priceAnnual: "990",
        description: "For teams that need visibility at scale.",
        cta: "Start Free, Upgrade Later",
        href: "/register",
        featured: false,
    },
    {
        name: "Enterprise",
        priceMonthly: "299",
        priceAnnual: "2990",
        description: "For organizations that demand the best.",
        cta: "Start Free, Upgrade Later",
        href: "/register",
        featured: false,
    },
]

type FeatureValue = boolean | string

interface FeatureRow {
    name: string
    free: FeatureValue
    pro: FeatureValue
    business: FeatureValue
    enterprise: FeatureValue
}

const featureGroups: { title: string; features: FeatureRow[] }[] = [
    {
        title: "Monitoring",
        features: [
            { name: "Targets", free: "5", pro: "25", business: "100", enterprise: "Unlimited" },
            { name: "Private agents", free: false, pro: "2", business: "5", enterprise: "10" },
            { name: "Shared agents (EU)", free: true, pro: true, business: true, enterprise: true },
            { name: "Shared agents (US + Asia)", free: false, pro: true, business: true, enterprise: true },
            { name: "Min probe interval", free: "5 min", pro: "1 min", business: "30 sec", enterprise: "15 sec" },
            { name: "Probe types (ICMP, TCP, HTTP, DNS)", free: true, pro: true, business: true, enterprise: true },
            { name: "Traceroute", free: true, pro: true, business: true, enterprise: true },
        ],
    },
    {
        title: "Data & Visualization",
        features: [
            { name: "Data retention", free: "7 days", pro: "90 days", business: "1 year", enterprise: "2 years" },
            { name: "Smoke graphs", free: true, pro: true, business: true, enterprise: true },
            { name: "Dashboard", free: true, pro: true, business: true, enterprise: true },
            { name: "Data export (GDPR)", free: true, pro: true, business: true, enterprise: true },
        ],
    },
    {
        title: "Alerting",
        features: [
            { name: "Alert rules", free: "3", pro: "25", business: "100", enterprise: "Unlimited" },
            { name: "Email notifications", free: true, pro: true, business: true, enterprise: true },
            { name: "Webhook notifications", free: true, pro: true, business: true, enterprise: true },
            { name: "Slack notifications", free: false, pro: true, business: true, enterprise: true },
            { name: "Discord notifications", free: false, pro: true, business: true, enterprise: true },
        ],
    },
    {
        title: "Team & Security",
        features: [
            { name: "Team members", free: "1", pro: "3", business: "10", enterprise: "Unlimited" },
            { name: "Role-based access (RBAC)", free: false, pro: true, business: true, enterprise: true },
            { name: "SSO (Google, GitHub)", free: false, pro: false, business: true, enterprise: true },
            { name: "Audit logs", free: false, pro: false, business: true, enterprise: true },
        ],
    },
    {
        title: "Support",
        features: [
            { name: "Community support", free: true, pro: true, business: true, enterprise: true },
            { name: "Email support", free: false, pro: "48h SLA", business: "24h SLA", enterprise: "4h SLA" },
            { name: "Priority support", free: false, pro: false, business: true, enterprise: true },
            { name: "Dedicated support", free: false, pro: false, business: false, enterprise: true },
        ],
    },
]

function FeatureCell({ value }: { value: FeatureValue }) {
    if (value === true) {
        return <Check className="h-4 w-4 text-primary mx-auto" />
    }
    if (value === false) {
        return <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />
    }
    return <span className="text-sm">{value}</span>
}

const faqs = [
    {
        q: "Can I switch plans at any time?",
        a: "Yes. Upgrade or downgrade whenever you want. Changes take effect immediately. We prorate automatically — no double-charging.",
    },
    {
        q: "What happens when I hit a limit?",
        a: "Soft limits. We won't break your monitoring. You'll see a banner suggesting an upgrade, but your existing targets keep running. We're not in the business of holding your uptime hostage.",
    },
    {
        q: "Where is my data stored?",
        a: "France (OVHCloud). All data stays in the EU. We use no GAFAM services — no AWS, no GCP, no Azure. Your data never leaves European soil.",
    },
    {
        q: "Do you offer annual billing?",
        a: "Yes! Annual billing saves you 2 months compared to monthly. Toggle between monthly and annual on the billing page.",
    },
    {
        q: "What payment methods do you accept?",
        a: "All major credit and debit cards, PayPal, and SEPA direct debit for EU customers. Payments are processed securely by LemonSqueezy, our Merchant of Record.",
    },
    {
        q: "Can I cancel anytime?",
        a: "Yes. No contracts, no cancellation fees, no dark patterns. Cancel and your account downgrades to Free at the end of the billing period. Your data stays until the retention window expires.",
    },
]

export default function PricingPage() {
    const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly")

    return (
        <div className="py-20">
            {/* Header */}
            <div className="text-center max-w-3xl mx-auto mb-10 px-4">
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-4">
                    Simple pricing. No games.
                </h1>
                <p className="text-muted-foreground text-lg">
                    Pay for what you use. Upgrade when you&apos;re ready. Cancel whenever. The entire feature set is transparent — no &quot;contact sales&quot; nonsense.
                </p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-12 px-4">
                <div className="flex items-center gap-1 rounded-xl border border-border/50 bg-card/30 p-1">
                    <button
                        onClick={() => setBillingCycle("monthly")}
                        className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                            billingCycle === "monthly"
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle("annual")}
                        className={`flex items-center gap-2 rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                            billingCycle === "annual"
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        Annual
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            Save 17%
                        </Badge>
                    </button>
                </div>
            </div>

            {/* Plan Cards */}
            <div className="container px-4 mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-24">
                    {plans.map((plan) => {
                        const price = billingCycle === "annual" ? plan.priceAnnual : plan.priceMonthly
                        const period = plan.priceMonthly === "0"
                            ? ""
                            : billingCycle === "annual" ? "/yr" : "/mo"
                        return (
                        <div
                            key={plan.name}
                            className={`relative rounded-2xl p-8 flex flex-col ${
                                plan.featured
                                    ? "border-2 border-primary bg-primary/5 shadow-[0_0_40px_rgba(var(--primary),0.15)]"
                                    : "border border-border/50 bg-card/30"
                            }`}
                        >
                            {plan.featured && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                                    Most Popular
                                </div>
                            )}
                            <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                            <p className="text-muted-foreground text-sm mb-6">{plan.description}</p>
                            <div className="mb-8">
                                <span className="text-5xl font-bold">&euro;{price}</span>
                                {period && (
                                    <span className="text-muted-foreground text-sm">{period}</span>
                                )}
                            </div>
                            {isComingSoon ? (
                                <Button
                                    variant={plan.featured ? "default" : "outline"}
                                    className="w-full mt-auto gap-2"
                                    disabled
                                >
                                    <Rocket className="h-3.5 w-3.5" />
                                    Launching Soon
                                </Button>
                            ) : (
                                <Button
                                    variant={plan.featured ? "default" : "outline"}
                                    className="w-full mt-auto"
                                    asChild
                                >
                                    <Link href={plan.href}>{plan.cta}</Link>
                                </Button>
                            )}
                        </div>
                        )
                    })}
                </div>

                {/* Feature Comparison Table */}
                <div className="max-w-6xl mx-auto mb-24">
                    <h2 className="text-2xl font-bold text-center mb-12">
                        Full feature comparison
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-border">
                                    <th className="text-left py-4 px-4 font-medium text-muted-foreground w-1/3">
                                        Feature
                                    </th>
                                    {plans.map((plan) => (
                                        <th
                                            key={plan.name}
                                            className={`text-center py-4 px-4 font-semibold ${
                                                plan.featured ? "text-primary" : ""
                                            }`}
                                        >
                                            {plan.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {featureGroups.map((group) => (
                                    <Group key={group.title} group={group} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* FAQ */}
                <div className="max-w-3xl mx-auto mb-24">
                    <h2 className="text-2xl font-bold text-center mb-12">
                        Frequently asked questions
                    </h2>
                    <div className="space-y-6">
                        {faqs.map((faq) => (
                            <div key={faq.q} className="border-b border-border/50 pb-6">
                                <h3 className="font-semibold mb-2">{faq.q}</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA */}
                <div className="text-center max-w-2xl mx-auto">
                    {isComingSoon ? (
                        <>
                            <h2 className="text-2xl font-bold mb-4">
                                We&apos;re almost ready.
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                PingNG is launching soon. Stay tuned.
                            </p>
                            <Button size="lg" className="h-12 px-8 gap-2" disabled>
                                <Rocket className="h-4 w-4" />
                                Launching Soon
                            </Button>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-bold mb-4">
                                Still reading? Just try it.
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                Free tier. No credit card. If it sucks, you&apos;ll know in 2 minutes.
                            </p>
                            <Button size="lg" className="h-12 px-8" asChild>
                                <Link href="/register">Start Monitoring Free</Link>
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

function Group({ group }: { group: typeof featureGroups[0] }) {
    return (
        <>
            <tr>
                <td
                    colSpan={5}
                    className="py-4 px-4 pt-8 text-sm font-semibold text-primary uppercase tracking-wider"
                >
                    {group.title}
                </td>
            </tr>
            {group.features.map((feature) => (
                <tr key={feature.name} className="border-b border-border/30 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4 text-sm">{feature.name}</td>
                    <td className="py-3 px-4 text-center">
                        <FeatureCell value={feature.free} />
                    </td>
                    <td className="py-3 px-4 text-center">
                        <FeatureCell value={feature.pro} />
                    </td>
                    <td className="py-3 px-4 text-center">
                        <FeatureCell value={feature.business} />
                    </td>
                    <td className="py-3 px-4 text-center">
                        <FeatureCell value={feature.enterprise} />
                    </td>
                </tr>
            ))}
        </>
    )
}
